import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import type { CrosstabCell } from '../data/types';
import { cn } from '../lib/cn';

export function SectionMarker({ titleEn, titleJp, className }: { titleEn: string; titleJp: string; className?: string }) {
  return (
    <header className={cn('grid min-w-0 gap-3', className)}>
      <p className="break-words text-[clamp(2.25rem,12vw,4.5rem)] font-bold uppercase tracking-[0.14em] text-atc-primary">{titleEn}</p>
      <h2 className="text-2xl font-bold tracking-[-0.02em] text-atc-ink md:text-3xl">{titleJp}</h2>
    </header>
  );
}

export function CurvedDivider({ direction = 'up', className }: { direction?: 'up' | 'down'; className?: string }) {
  return (
    <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className={cn('block h-16 w-full text-atc-base md:h-24', direction === 'down' && 'rotate-180', className)} aria-hidden="true">
      <path d="M0 120V62C270 4 465 98 735 48C1008 0 1190 73 1440 20V120Z" fill="currentColor" />
    </svg>
  );
}

const chartFrame = cva('rounded-3xl bg-white p-6 shadow-atc-card md:p-8', {
  variants: { emphasis: { default: '', strong: 'ring-2 ring-atc-navy/10' } },
  defaultVariants: { emphasis: 'default' },
});

interface ChartFrameProps extends ComponentPropsWithoutRef<'section'>, VariantProps<typeof chartFrame> {
  title: string;
  description?: string;
  children: ReactNode;
}

export function ChartFrame({ title, description, children, emphasis, className, ...props }: ChartFrameProps) {
  return (
    <section className={cn(chartFrame({ emphasis }), className)} {...props}>
      <div className="mb-6">
        <h3 className="text-lg font-bold text-atc-ink">{title}</h3>
        {description && <p className="mt-2 text-sm leading-relaxed text-atc-slate">{description}</p>}
      </div>
      {children}
    </section>
  );
}

export function DataLabel({ value, label, period }: { value: string | number; label: string; period?: string }) {
  return (
    <div className="grid gap-1">
      <strong className="text-4xl font-bold tabular-nums tracking-[-0.04em] text-atc-ink md:text-5xl">{value}</strong>
      <span className="text-sm font-medium text-atc-ink">{label}</span>
      {period && <span className="text-xs font-medium text-atc-slate">{period}</span>}
    </div>
  );
}

export function ReferenceValueMarker({ cell }: { cell: CrosstabCell }) {
  if (!cell.small) return null;
  return <span className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full bg-atc-accent" role="img" aria-label="参考値" title="参考値" />;
}

export function ReferenceValueLegend() {
  return (
    <p className="inline-flex items-center gap-2 text-xs font-medium text-atc-slate">
      <span className="h-2.5 w-2.5 rounded-full bg-atc-accent" aria-hidden="true" />
      回答数30未満は参考値
    </p>
  );
}

export function normalizeCrosstabCell(cell: CrosstabCell | undefined): CrosstabCell {
  return cell ?? { n: 0, small: true };
}
